import { FILE_TYPE_DISPLAY, type FileTypeId } from '@steerfi/shared';
import { IconArrowBackUp, IconFile, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useMemo } from 'react';
import type { FileFormatApiRow } from '@/api/file-formats';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from '@/components/data-table';
import type { RowActivatorLink } from '@/components/row-activator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/utils';

const SkeletonRow = () => (
  <div className="flex items-center gap-4">
    <Skeleton className="h-4 w-24" />
    <Skeleton className="h-4 w-40" />
    <Skeleton className="h-4 w-16" />
    <Skeleton className="h-4 w-14" />
    <Skeleton className="ml-auto h-4 w-20" />
  </div>
);

interface FileFormatsTableProps {
  data: DataTableResponse<FileFormatApiRow> | undefined;
  isLoading: boolean;
  error: string | null;
  currentFilterValues: Record<string, string | string[]>;
  currentSortKey: string;
  currentSortOrder: 'asc' | 'desc';
  searchValue: string;
  onFilterChange: (filterKey: string, value: string | string[]) => void;
  onSortChange: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange: (page: number) => void;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (row: FileFormatApiRow) => void;
  onDelete: (row: FileFormatApiRow) => void;
  onRestore: (row: FileFormatApiRow) => void;
  /**
   * Row-level click handler. Bound to the underlying DataTable's
   * `onRowClick`; the actions cell stops propagation so pencil/trash
   * buttons keep their own handlers.
   */
  onRowClick?: (row: FileFormatApiRow) => void;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((row: FileFormatApiRow) => RowActivatorLink) | undefined;
  /** Column keys to omit from both the table and the filter bar. */
  hiddenColumns?: ReadonlySet<string>;
  /** Hide the header "Add File Format" button when the parent already provides one. */
  hideHeaderAdd?: boolean;
}

const FILTERS: FilterConfig[] = [
  {
    key: 'scope',
    label: 'Scope',
    options: [
      { value: 'system', label: 'System' },
      { value: 'provider', label: 'Provider' },
      { value: 'client', label: 'Client' },
    ],
    placeholder: 'All',
  },
  // Status is a strict toggle: a row is either active or soft-deleted,
  // those are the only two states (see the FileFormat entity -- it has
  // no status column, only a deleted_at timestamp). `hideAllOption`
  // suppresses DataTable's auto-injected "All" item so the dropdown
  // only ever offers the two real states; the default selection is
  // 'active' (DEFAULTS.STATUS in use-file-formats.ts).
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'deleted', label: 'Deleted' },
    ],
    hideAllOption: true,
  },
];

export const FileFormatsTable: React.FC<FileFormatsTableProps> = ({
  data,
  isLoading,
  error,
  currentFilterValues,
  currentSortKey,
  currentSortOrder,
  searchValue,
  onFilterChange,
  onSortChange,
  onPageChange,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  onRestore,
  onRowClick,
  getRowLink,
  hiddenColumns,
  hideHeaderAdd,
}) => {
  const allColumns: ColumnConfig<FileFormatApiRow>[] = useMemo(
    () => [
      {
        key: 'code',
        label: 'Code',
        sortable: true,
        render: (item) => <span className="font-mono text-sm">{item.code}</span>,
      },
      {
        key: 'format_name',
        label: 'Name',
        sortable: true,
        render: (item) => <span className="font-medium">{item.format_name}</span>,
      },
      {
        key: 'file_type_id',
        label: 'Type',
        sortable: true,
        render: (item) =>
          FILE_TYPE_DISPLAY[item.file_type_id as FileTypeId] ?? String(item.file_type_id),
      },
      {
        key: 'scope',
        label: 'Scope',
        sortable: false,
        render: (item) => {
          if (item.party_id == null) {
            return (
              <Badge variant="secondary" className="bg-sky-100 text-sky-900 hover:bg-sky-100">
                System-wide
              </Badge>
            );
          }
          return (
            <div className="flex flex-wrap items-center gap-2">
              <span>{item.party?.display_name ?? '—'}</span>
              {item.party?.client_code ? (
                <Badge variant="outline" className="text-xs uppercase">
                  {item.party.client_code}
                </Badge>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'version_number',
        label: 'Version',
        sortable: false,
        render: (item) => `v${item.version_number}`,
      },
      {
        key: 'updated_at',
        label: 'Updated',
        sortable: true,
        align: 'right',
        render: (item) => (
          <span className="text-muted-foreground">
            {item.updated_at ? formatDate(item.updated_at) : '—'}
          </span>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        sortable: false,
        align: 'center',
        render: (item) => {
          if (item.deleted_at != null) {
            return (
              <div className="flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Restore ${item.code}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore(item);
                  }}
                >
                  <IconArrowBackUp size={14} />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center justify-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Edit ${item.code}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
              >
                <IconPencil size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                aria-label={`Delete ${item.code}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                }}
              >
                <IconTrash size={14} />
              </Button>
            </div>
          );
        },
      },
    ],
    [onEdit, onDelete, onRestore]
  );

  const columns = useMemo(
    () => (hiddenColumns?.size ? allColumns.filter((c) => !hiddenColumns.has(c.key)) : allColumns),
    [allColumns, hiddenColumns]
  );

  const visibleFilters = useMemo(
    () => (hiddenColumns?.size ? FILTERS.filter((f) => !hiddenColumns.has(f.key)) : FILTERS),
    [hiddenColumns]
  );

  const statusFilter = Array.isArray(currentFilterValues.status)
    ? currentFilterValues.status[0]
    : currentFilterValues.status;

  const emptyMessage = useMemo(() => {
    if (statusFilter === 'active') {
      return (
        <div className="flex flex-col items-center gap-2 py-4">
          <IconFile className="h-7 w-7 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium">No active file formats</p>
          <p className="text-xs text-muted-foreground">
            Add a file format, or switch to the Deleted filter to view and restore deleted formats.
          </p>
          <div className="mt-1 flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onAdd}>
              <IconPlus className="mr-1.5 h-3.5 w-3.5" />
              Add File Format
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onFilterChange('status', 'deleted')}
            >
              View deleted
            </Button>
          </div>
        </div>
      );
    }

    if (statusFilter === 'deleted') {
      return (
        <div className="flex flex-col items-center gap-2 py-4">
          <IconFile className="h-7 w-7 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium">No deleted file formats</p>
          <p className="text-xs text-muted-foreground">All file formats are active.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-1"
            onClick={() => onFilterChange('status', 'active')}
          >
            View active formats
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <IconFile className="h-7 w-7 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">No file formats found</p>
        <Button type="button" variant="outline" size="sm" className="mt-1" onClick={onAdd}>
          <IconPlus className="mr-1.5 h-3.5 w-3.5" />
          Add File Format
        </Button>
      </div>
    );
  }, [statusFilter, onAdd, onFilterChange]);

  const searchFilter = (
    <>
      <Label htmlFor="file-formats-search" className="text-sm font-medium">
        Search
      </Label>
      <Input
        id="file-formats-search"
        type="search"
        className="w-[260px]"
        placeholder="Search by code or name…"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
      />
    </>
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      caption="File formats"
      filters={visibleFilters}
      currentFilterValues={currentFilterValues}
      currentSortKey={currentSortKey}
      currentSortOrder={currentSortOrder}
      isLoading={isLoading}
      error={error}
      onFilterChange={onFilterChange}
      onSortChange={onSortChange}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
      // The first column is the code, but the label below names the format, and
      // an activator whose visible text isn't in its accessible name can't be
      // reached by voice control (WCAG 2.5.3). Naming the column keeps the two
      // in agreement, and puts the link on the name as every other table does.
      rowActivatorColumnKey="format_name"
      getRowLabel={(item) => `Open file format ${item.format_name}`}
      {...(getRowLink && { getRowLink })}
      extraFilters={searchFilter}
      headerActions={
        hideHeaderAdd ? undefined : (
          <Button onClick={onAdd}>
            <IconPlus className="mr-2 h-4 w-4" />
            Add File Format
          </Button>
        )
      }
      emptyMessage={emptyMessage}
      loadingMessage={
        <div className="flex flex-col gap-3 py-2">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      }
      defaultSortKey="format_name"
      defaultSortOrder="asc"
    />
  );
};
